'use client'
import { Assessments, MbtiResults } from '@crystal-eyes/types'
import SectionCard from '../../components/SectionCard'
import { Icons } from '@crystal-eyes/components/elements/Icon/Icon'
import styles from './Mbti.module.scss'
import assessmentStyles from './Assessments.module.scss'
import Link from 'next/link'
import useIsOwnProfile from '@crystal-eyes/hooks/profiles/context/useIsOwnProfile'
import useTakingAssessment from '../../hooks/useTakingAssessment'
import useKnowYourType from '../../hooks/useKnowYourType'

interface Identity {
  id: string
}

export default function Mbti({
  identity,
  assessments,
}: {
  identity: Identity
  assessments?: Assessments
}) {
  const { data: isOwnProfile } = useIsOwnProfile({ identity })
  const {
    assessmentState: { setTaking: setTakingAssessment },
  } = useTakingAssessment()
  const {
    entryState: { setKnowType },
  } = useKnowYourType()

  const mbti = assessments?.mbti

  if (!isOwnProfile && !mbti) return null
  return (
    <SectionCard
      cardId="myers_briggs"
      icon={Icons.PersonalityTypes16}
      title="16-Personality Types"
      sidebarTitle="16-Personality types"
      subtitle="based on types by Jung, Myers, & Briggs"
      showPrint={true}
      className={`${assessmentStyles.assessment} ${styles.mbti}`}
    >
      {!mbti && (
        <div className="take-assessment-cta">
          <div className="visualization">
            <Visualization />
          </div>

          <div className="cta">
            <h4>Find out your 16-Personality Type</h4>

            <p>
              Answer a few additional questions to learn more about your
              16-Personality Type.
            </p>

            <button
              className="CE action-button"
              onClick={evt => {
                evt.preventDefault()
                setTakingAssessment('mbti')
              }}
            >
              Start Test
            </button>

            <br />

            <Link
              href="#"
              className="already-know-link"
              onClick={evt => {
                evt.preventDefault()
                setKnowType('mbti')
              }}
            >
              Already know your type?
            </Link>
          </div>
        </div>
      )}

      {mbti && (
        <div className="assessment-visualization-results">
          <div className="visualization">
            <Visualization selectedTraits={mbti?.traits || []} />
          </div>

          <div className="overview">
            <span className="subtitle">Enneagram Type</span>
            <h4 className="CE Parented disc-color">{mbti.type}</h4>

            <p>{mbti.description}</p>

            {isOwnProfile && (
              <div className="retake-cta">
                <Link
                  href="#"
                  onClick={evt => {
                    evt.preventDefault()
                    setTakingAssessment('mbti')
                  }}
                >
                  Re-take the assessment
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </SectionCard>
  )
}

const Visualization = ({
  selectedTraits,
}: {
  selectedTraits?: MbtiResults['traits']
}) => {
  const selected = selectedTraits?.map(trait => trait.leansTowards)

  return (
    <div className="mbti-options">
      <div className="CE Parented disc-background-light bg-circle" />
      <div className="options">
        <div className="option">
          <div
            className={`CE Parented tooltip disc-background
              ${selected?.includes('Extroverted') ? 'selected' : 'unselected'}
            `}
          >
            Extroverted
            <div className="tooltip-content">
              Extroverts tend to draw energy from spending time around other
              people.
            </div>
          </div>
          <div
            className={`CE Parented tooltip disc-background
              ${selected?.includes('Introverted') ? 'selected' : 'unselected'}
            `}
          >
            Introverted
            <div className="tooltip-content">
              Introverts tend to draw energy from spending time alone.
            </div>
          </div>
        </div>

        <div className="option">
          <div
            className={`CE Parented tooltip disc-background
              ${selected?.includes('Sensing') ? 'selected' : 'unselected'}
            `}
          >
            Sensing
            <div className="tooltip-content">
              Sensing types usually prefer to experience new things and focus on
              the reality of the situation.
            </div>
          </div>
          <div
            className={`CE Parented tooltip disc-background
              ${selected?.includes('Intuitive') ? 'selected' : 'unselected'}
            `}
          >
            Intuitive
            <div className="tooltip-content">
              Intuitive types tend to love to explore new ideas and think in
              terms of the big picture.
            </div>
          </div>
        </div>

        <div className="option">
          <div
            className={`CE Parented tooltip disc-background
              ${selected?.includes('Thinking') ? 'selected' : 'unselected'}
            `}
          >
            Thinking
            <div className="tooltip-content">
              Thinking types tend to follow their head and consider facts when
              making decisions.
            </div>
          </div>
          <div
            className={`CE Parented tooltip disc-background
              ${selected?.includes('Feeling') ? 'selected' : 'unselected'}
            `}
          >
            Feeling
            <div className="tooltip-content">
              Feeling types usually prioritize emotion over logic, often
              choosing to follow their gut when making decisions.
            </div>
          </div>
        </div>

        <div className="option">
          <div
            className={`CE Parented tooltip disc-background
              ${selected?.includes('Judging') ? 'selected' : 'unselected'}
            `}
          >
            Judging
            <div className="tooltip-content">
              Judging types tend to be very structured and organized.
            </div>
          </div>
          <div
            className={`CE Parented tooltip disc-background
              ${selected?.includes('Perceiving') ? 'selected' : 'unselected'}
            `}
          >
            Perceiving
            <div className="tooltip-content">
              Perceiving types usually prefer to be flexible and spontaneous.
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
